'use client';

import { useTranslations } from 'next-intl';
import { ScanLine, FileText, Loader2, Eye } from 'lucide-react';
import { useRouter } from '@/i18n/routing';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { RouteGate } from '@/components/route-gate';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { usePermissions } from '@/hooks/use-permissions';
import { RepairHistoryButton } from './repair-history-button';
import type { BatchStatus, ScanBatch } from '../types';

interface PhotoScanningCardProps {
  batch: ScanBatch;
  onStartScanning: (batch: ScanBatch) => void;
  onViewPdf: (batch: ScanBatch) => void;
  isScanningDisabled: (batch: ScanBatch) => boolean;
  getScanDisabledReason: (batch: ScanBatch) => string | null;
  getScanButtonLabel: (batch: ScanBatch) => string;
  isStartPending: boolean;
  isPdfLoading: boolean;
}

const STATUS_STYLES: Record<BatchStatus, string> = {
  pending: 'border-gray-500/30 bg-gray-500/10 text-gray-600 dark:text-gray-400',
  confirmed:
    'border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400',
  in_progress:
    'border-blue-500/30 bg-blue-500/10 text-blue-600 dark:text-blue-400',
  completed:
    'border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
};

const STATUS_LABEL_KEYS: Record<BatchStatus, string> = {
  pending: 'statusPending',
  confirmed: 'statusAssigned',
  in_progress: 'statusInProgress',
  completed: 'statusCompleted'
};

export function PhotoScanningCard({
  batch,
  onStartScanning,
  onViewPdf,
  isScanningDisabled,
  getScanDisabledReason,
  getScanButtonLabel,
  isStartPending,
  isPdfLoading
}: PhotoScanningCardProps) {
  const t = useTranslations('photoScanning');
  const router = useRouter();
  const { hasPermission } = usePermissions();
  const canSeeContainer = hasPermission(
    'photo_scanning.column.columnContainerId'
  );
  const canSeeScanner = hasPermission('photo_scanning.column.columnScanner');
  const canSeeStatus = hasPermission('photo_scanning.column.columnStatus');

  const pct =
    batch.total_pages > 0
      ? Math.round((batch.scanned_pages / batch.total_pages) * 100)
      : 0;

  const disabled = isScanningDisabled(batch);
  const disabledReason = getScanDisabledReason(batch);

  return (
    <Card className='border-border hover:border-primary/30 transition-colors'>
      <CardContent className='p-5'>
        {/* Top: batch number + status */}
        <div className='mb-3 flex items-start justify-between'>
          <span className='font-mono text-sm font-bold'>
            {batch.batch_number}
          </span>
          {canSeeStatus && (
            <Badge
              variant='outline'
              className={cn(
                'shrink-0 text-xs uppercase',
                STATUS_STYLES[batch.status]
              )}
            >
              {STATUS_LABEL_KEYS[batch.status]
                ? t(STATUS_LABEL_KEYS[batch.status])
                : batch.status}
            </Badge>
          )}
        </div>

        {/* Middle: details */}
        <div className='text-muted-foreground mb-3 space-y-1.5 text-sm'>
          {canSeeContainer && (
            <p>
              <span className='font-medium'>{t('columnContainerId')}:</span>{' '}
              <span className='font-mono'>{batch.container_id}</span>
            </p>
          )}
          <p>
            <span className='font-medium'>{t('clientName')}:</span>{' '}
            {batch.client_name}
          </p>
          {canSeeScanner && (
            <p className='flex items-center gap-1.5'>
              <span className='font-medium'>{t('columnScanner')}:</span>
              <span
                className={cn(
                  'inline-block h-2 w-2 rounded-full',
                  batch.scanner_status === 'online'
                    ? 'bg-emerald-500'
                    : batch.scanner_status === 'maintenance'
                      ? 'bg-amber-500'
                      : 'bg-rose-500'
                )}
              />
              <span className='font-mono'>
                {batch.scanner_id} — {batch.scanner_model}
              </span>
            </p>
          )}
          {batch.region && (
            <p>
              <span className='font-medium'>{t('region')}:</span> {batch.region}
            </p>
          )}
        </div>

        {/* Progress bar */}
        <div className='mb-3'>
          <div className='mb-1 flex items-center justify-between text-xs'>
            <span className='text-muted-foreground'>
              {t('pagesRatio', {
                done: batch.scanned_pages,
                total: batch.total_pages
              })}
            </span>
            <span className='text-muted-foreground'>{pct}%</span>
          </div>
          <div className='bg-muted h-1.5 w-full overflow-hidden rounded-full'>
            <div
              className='h-full rounded-full bg-blue-500 transition-all'
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        {/* Bottom: actions */}
        <div className='flex items-center justify-end gap-1 border-t pt-3'>
          {batch.registration_id != null && (
            <RouteGate route={`/dashboard/containers/${batch.registration_id}`}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size='icon'
                    variant='ghost'
                    onClick={() =>
                      router.push(
                        `/dashboard/containers/${batch.registration_id}`
                      )
                    }
                    className='h-8 w-8'
                  >
                    <Eye className='h-4 w-4' />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t('viewContainer')}</TooltipContent>
              </Tooltip>
            </RouteGate>
          )}
          {batch.status === 'completed' ? (
            <Button
              size='sm'
              variant='outline'
              onClick={() => onViewPdf(batch)}
              disabled={isPdfLoading}
              className='gap-1.5'
            >
              {isPdfLoading ? (
                <Loader2 className='h-3.5 w-3.5 animate-spin' />
              ) : (
                <FileText className='h-3.5 w-3.5' />
              )}
              {t('viewPdf')}
            </Button>
          ) : disabled && disabledReason ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className='inline-block'>
                  <Button
                    size='sm'
                    disabled
                    className='pointer-events-none opacity-50'
                  >
                    <ScanLine className='me-1.5 h-3.5 w-3.5' />
                    {getScanButtonLabel(batch)}
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>{disabledReason}</TooltipContent>
            </Tooltip>
          ) : (
            <Button
              size='sm'
              onClick={() => onStartScanning(batch)}
              disabled={isStartPending}
            >
              <ScanLine className='me-1.5 h-3.5 w-3.5' />
              {getScanButtonLabel(batch)}
            </Button>
          )}
          {batch.repair_count > 0 && (
            <RepairHistoryButton batch={batch} label={t('viewRepairHistory')} />
          )}
        </div>
      </CardContent>
    </Card>
  );
}
